/**
 * URL helpers for the Post Comments page (`/post-comments`).
 * Kept under `inbox/comments` so the same module can be mounted from Inbox later
 * without duplicating business state — only the host route changes.
 */
export type CommentsViewMode = "comments" | "posts";

/** Optional future Inbox remount (`InboxWorkspaceSwitcher`); unused by V1 host route. */
export type InboxWorkspace = "messages" | "comments";

export function parseInboxWorkspace(raw: string | null): InboxWorkspace {
  return raw === "comments" ? "comments" : "messages";
}

export function withInboxWorkspace(
  current: URLSearchParams,
  workspace: InboxWorkspace,
): URLSearchParams {
  const next = new URLSearchParams(current);
  if (workspace === "messages") {
    next.delete("workspace");
    next.delete("viewMode");
    next.delete("commentId");
    next.delete("postId");
  } else {
    next.set("workspace", "comments");
    next.delete("conversation");
  }
  return next;
}

export function parseCommentsViewMode(raw: string | null): CommentsViewMode {
  return raw === "posts" ? "posts" : "comments";
}

export function withCommentsViewMode(
  current: URLSearchParams,
  viewMode: CommentsViewMode,
): URLSearchParams {
  const next = new URLSearchParams(current);
  if (viewMode === "comments") {
    next.delete("viewMode");
    next.delete("postId");
  } else {
    next.set("viewMode", "posts");
  }
  return next;
}

export function withSelectedCommentId(
  current: URLSearchParams,
  commentId: string | null,
): URLSearchParams {
  const next = new URLSearchParams(current);
  if (commentId == null) {
    next.delete("commentId");
  } else {
    next.set("commentId", commentId);
  }
  return next;
}

export function withSelectedPostId(
  current: URLSearchParams,
  postId: string | null,
): URLSearchParams {
  const next = new URLSearchParams(current);
  // Drill into Mode A filtered by the chosen post (keeps postId chip + comments list).
  next.delete("viewMode");
  if (postId == null) {
    next.delete("postId");
  } else {
    next.set("postId", postId);
  }
  next.delete("commentId");
  return next;
}

/** Deep-link helper for future CommentOriginBanner → Post Comments navigation. */
export function buildPostCommentsCommentPath(commentId: string): string {
  const params = new URLSearchParams();
  params.set("commentId", commentId);
  return `/post-comments?${params.toString()}`;
}

/** Open an existing verified DM conversation from a comment. */
export function buildInboxConversationPath(conversationId: number): string {
  return `/inbox?conversation=${conversationId}`;
}
